import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { matchesExpertiseSearch } from "../lib/clinicalOptions";

const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const DirectoryMultiSelect = ({
  value = "",
  onChange,
  options = [],
  testId = "filter-specialty",
  emptyLabel = "Any specialty",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = useMemo(
    () => (value ? value.split(",").map((item) => item.trim()).filter(Boolean) : []),
    [value]
  );

  const toggle = (option) => {
    const next = selected.includes(option)
      ? selected.filter((item) => item !== option)
      : [...selected, option];
    onChange(next.join(", "));
  };
  const visibleOptions = useMemo(
    () => options.filter((option) => matchesExpertiseSearch(option, query)),
    [options, query]
  );

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId + "-trigger"}
          aria-label="Select specialties"
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className={selected.length ? "truncate text-slate-900" : "text-slate-500"}>
            {selected.length ? selected.length + " selected" : emptyLabel}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] bg-white p-0" align="start">
        <div className="border-b border-slate-200 p-2">
          <div className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5">
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search expertise"
              aria-label="Search clinical expertise"
              className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
              data-testid={testId + "-search"}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {visibleOptions.map((option) => {
            const checked = selected.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => toggle(option)}
                data-testid={testId + "-option-" + slug(option)}
                className="flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-sm hover:bg-slate-100"
              >
                <span
                  className={"flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border " + (checked ? "border-primary bg-primary text-white" : "border-slate-300 bg-white")}
                  aria-hidden="true"
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1 text-slate-700">{option}</span>
              </button>
            );
          })}
          {visibleOptions.length === 0 && <p className="px-3 py-4 text-center text-sm text-slate-500">No matching expertise</p>}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-white p-2">
          <button
            type="button"
            onClick={() => onChange("")}
            className="px-2 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
            data-testid={testId + "-clear"}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white"
            data-testid={testId + "-apply"}
          >
            Apply
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
